import React, { useState } from 'react';

interface ConversionResult {
  originalValue: number;
  significantDigits: number;
  convertedValue: string;
  convertedValueNormal: string;
  explanation: string;
}

export const SignificantFigureConverter: React.FC = () => {
  const [value, setValue] = useState<string>('');
  const [significantDigits, setSignificantDigits] = useState<string>('');
  const [result, setResult] = useState<ConversionResult | null>(null);

  const convertToSignificantFigures = () => {
    const numValue = parseFloat(value);
    const digits = parseInt(significantDigits);
    
    if (isNaN(numValue) || isNaN(digits) || digits <= 0) {
      setResult(null);
      return;
    }

    const convertedValue = formatWithSignificantFigures(numValue, digits);
    const convertedValueNormal = formatNormalNotation(numValue, digits);
    const explanation = generateExplanation(numValue, digits, convertedValue);
    
    setResult({
      originalValue: numValue,
      significantDigits: digits,
      convertedValue,
      convertedValueNormal,
      explanation
    });
  };

  const formatWithSignificantFigures = (num: number, significantDigits: number): string => {
    if (num === 0) return '0';
    
    const magnitude = Math.floor(Math.log10(Math.abs(num))) + 1;
    
    if (magnitude >= significantDigits) {
      // 整数部分が有効数字より大きい場合
      return num.toExponential(significantDigits - 1);
    } else {
      // 小数点以下に有効数字がある場合
      const decimalPlaces = significantDigits - magnitude;
      return num.toFixed(Math.max(0, decimalPlaces));
    }
  };

  const formatNormalNotation = (num: number, significantDigits: number): string => {
    if (num === 0) return '0';
    
    const magnitude = Math.floor(Math.log10(Math.abs(num))) + 1;
    
    if (magnitude >= significantDigits) {
      // 科学表記の場合、通常の表記も計算
      const coefficient = num / Math.pow(10, magnitude - 1);
      const roundedCoefficient = Math.round(coefficient * Math.pow(10, significantDigits - 1)) / Math.pow(10, significantDigits - 1);
      return (roundedCoefficient * Math.pow(10, magnitude - 1)).toString();
    } else {
      // 小数点以下に有効数字がある場合
      const decimalPlaces = significantDigits - magnitude;
      return num.toFixed(Math.max(0, decimalPlaces));
    }
  };

  const generateExplanation = (num: number, digits: number, converted: string): string => {
    const magnitude = Math.floor(Math.log10(Math.abs(num))) + 1;
    
    if (magnitude >= digits) {
      return `元の数値 ${num} は ${magnitude} 桁の整数部分がありますが、指定された有効数字は ${digits} 桁です。そのため、科学表記で表示しています。`;
    } else {
      const decimalPlaces = digits - magnitude;
      return `元の数値 ${num} を ${digits} 桁の有効数字で表すと、小数点以下 ${decimalPlaces} 桁まで表示されます。`;
    }
  };

  return (
    <div className="card">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">有効桁数変換ツール</h3>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            計算で出た値
          </label>
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="例: 123.456789"
            className="input-field"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            指定された有効桁数
          </label>
          <input
            type="number"
            value={significantDigits}
            onChange={(e) => setSignificantDigits(e.target.value)}
            placeholder="例: 3"
            className="input-field"
            min="1"
            max="10"
          />
        </div>
        
        <button
          onClick={convertToSignificantFigures}
          className="btn-primary w-full"
        >
          有効桁数に変換
        </button>
      </div>

      {result && (
        <div className="mt-6 space-y-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">変換結果</h4>
                         <div className="space-y-2">
               <div className="flex justify-between">
                 <span className="text-blue-700">元の値:</span>
                 <span className="font-mono text-blue-900">{result.originalValue}</span>
               </div>
               <div className="flex justify-between">
                 <span className="text-blue-700">有効桁数:</span>
                 <span className="font-mono text-blue-900">{result.significantDigits}桁</span>
               </div>
               <div className="flex justify-between">
                 <span className="text-blue-700">変換後:</span>
                 <span className="font-mono text-blue-900 font-bold text-lg">{result.convertedValue}</span>
               </div>
               {result.convertedValue.includes('e') && (
                 <div className="flex justify-between">
                   <span className="text-blue-700">通常表記:</span>
                   <span className="font-mono text-blue-900">{result.convertedValueNormal}</span>
                 </div>
               )}
             </div>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg">
            <h4 className="font-medium text-green-900 mb-2">解説</h4>
            <p className="text-green-800 text-sm">{result.explanation}</p>
          </div>
        </div>
      )}
    </div>
  );
}; 